import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

import type { ProgramContext, V2SearchResponse } from '@/types/V2SearchTypes'
import {
    ELITE_MEMBERSHIP_CHANGE_DATE_FEE,
    ELITE_MEMBERSHIP_STRIP_GUEST,
    ELITE_MEMBERSHIP_STRIP_INACTIVE_ZERO,
    ELITE_MEMBERSHIP_STRIP_PROGRESS,
    ELITE_MEMBERSHIP_STRIP_TRACKER_EXPIRING_SOON
} from '@/constants/components/eliteMembership'
import { isLoyaltyDisplayStatusSuspended } from '@/utils/homepage/elite'

dayjs.extend(customParseFormat)

const getProgramContext = (data: V2SearchResponse | null | undefined): ProgramContext | undefined => data?.programContext

const LOYALTY_DATE_FORMATS = ['DD-MM-YYYY', 'YYYY-MM-DD'] as const

export type UnlockElitePillState = {
    stripLabel: string
    showLogin: boolean
    changeDateFee: number
}

export const isEliteFeatureEligible = (processedData?: { isIntl?: boolean }) =>
    !processedData?.isIntl

/** Virtualized list index at which the elite banner is injected (before that card). */
export const getEliteBannerListIndex = (processedData?: { isCfw?: boolean }): number =>
    processedData?.isCfw ? 2 : 3

export const isV2LoyaltyStatusActive = (data: V2SearchResponse | null | undefined): boolean => {
    const status = getProgramContext(data)?.loyaltyTrackerDetails?.[0]?.loyaltyStatus
    return typeof status === 'string' && status.toUpperCase() === 'ACTIVE'
}

export const getV2ChangeDateFee = (data: V2SearchResponse | null | undefined): number | null => {
    const eliteBenefit = getProgramContext(data)?.benefitDetails?.find(
        item => item.programName === 'ELITE' && item.eligible
    )
    const clearChoicePlus = eliteBenefit?.benefits?.find(item => item.name === 'CLEARCHOICE_PLUS')
    const value = clearChoicePlus?.discountDetail?.value

    return typeof value === 'number' ? value : null
}

const getChangeDateFee = (data: V2SearchResponse | null | undefined): number =>
    getV2ChangeDateFee(data) ?? ELITE_MEMBERSHIP_CHANGE_DATE_FEE

const formatLoyaltyDisplayDate = (date: string | null | undefined): string | null => {
    if (!date) {
        return null
    }

    for (const format of LOYALTY_DATE_FORMATS) {
        const parsed = dayjs(date, format, true)
        if (parsed.isValid()) {
            return parsed.format('D MMMM YYYY')
        }
    }

    const parsed = dayjs(date)
    return parsed.isValid() ? parsed.format('D MMMM YYYY') : null
}

const getPendingBookings = (data: V2SearchResponse | null | undefined): number | null => {
    const constraint = getProgramContext(data)?.loyaltyTrackerDetails?.[0]?.tierTrackerDetails?.[0]?.constraintList?.find(
        item => item.type === 'COUNT'
    )
    return constraint?.pending ?? null
}

const getInactiveStripLabel = (data: V2SearchResponse): string => {
    const loyaltyTracker = getProgramContext(data)?.loyaltyTrackerDetails?.[0]
    const tierTracker = loyaltyTracker?.tierTrackerDetails?.[0]
    const displayStatus = loyaltyTracker?.displayStatus?.toUpperCase()

    if (displayStatus === 'TRACKER_EXPIRING_SOON') {
        const formattedDate = formatLoyaltyDisplayDate(tierTracker?.viewExpiryDate)
        if (formattedDate) {
            return ELITE_MEMBERSHIP_STRIP_TRACKER_EXPIRING_SOON(formattedDate)
        }
    }

    const pending = getPendingBookings(data)

    return pending != null && pending <= 1
        ? ELITE_MEMBERSHIP_STRIP_PROGRESS
        : ELITE_MEMBERSHIP_STRIP_INACTIVE_ZERO
}

const isLoyaltyTrackerSuspended = (loyaltyTracker?: {
    loyaltyStatus?: string | null
    displayStatus?: string | null
}): boolean =>
    isLoyaltyDisplayStatusSuspended(loyaltyTracker?.displayStatus) ||
    (loyaltyTracker?.loyaltyStatus ?? '').trim().toUpperCase() === 'SUSPENDED'

export const getUnlockElitePillState = (
    data: V2SearchResponse | null | undefined,
    isLoggedIn: boolean,
    isLoading: boolean
): UnlockElitePillState | null => {
    if (isLoading) {
        return null
    }

    const loyaltyTracker = getProgramContext(data)?.loyaltyTrackerDetails?.[0]
    const changeDateFee = getChangeDateFee(data)

    if (loyaltyTracker && isLoyaltyTrackerSuspended(loyaltyTracker)) {
        return null
    }

    if (!isLoggedIn) {
        return { stripLabel: ELITE_MEMBERSHIP_STRIP_GUEST, showLogin: true, changeDateFee }
    }

    if (!loyaltyTracker) {
        return null
    }

    if (loyaltyTracker.loyaltyStatus?.toUpperCase() !== 'INACTIVE') {
        return null
    }

    // Membership already earned and waiting to activate — hide unlock widget
    if (loyaltyTracker.displayStatus?.toUpperCase() === 'ACTIVATING_SOON') {
        return null
    }

    return {
        stripLabel: getInactiveStripLabel(data as V2SearchResponse),
        showLogin: false,
        changeDateFee
    }
}
